import { runTrackedAudioWork, swallowAudioWorkCutoff } from '../../audioWorkBarrier';
import { type RecordingKey } from '../../persistenceV2/types';
import { type BackupRecording } from '../../types';
import { BackupCaptureRecorder } from './BackupCaptureRecorder';
import { handleBackupAvailableV2 } from './backupV2';
import { discardReplacedCaptureSlot, stopCaptureSlot } from './captureSlotLifecycle';
import { requireConfig, type GetState, type SetState } from './storeTypes';

/** Owns the single live backup capture without loading the audio-graph store. */

export const startBackupCaptureV2 = (
  set: SetState,
  get: GetState,
  stream: MediaStream,
  recordingKey: RecordingKey
) => {
  const { sessionId, recordingId } = recordingKey;

  discardReplacedCaptureSlot({
    slot: get().backupCaptureSlot,
    replacedBy: recordingId,
    eventType: 'BACKUP_CAPTURE_RECORDER_REPLACED',
  });

  const config = requireConfig(get);
  const backupCaptureRecorder = new BackupCaptureRecorder({
    format: config.format,
    codec: config.codec,
    stream,
    recordingId,
    onBackup: (blob: Blob) => {
      const backup: BackupRecording = {
        sessionId,
        recordingId,
        isProcessed: false,
        blob,
      };
      // Awaited by the recorder so stop() settles at the backup persist.
      return runTrackedAudioWork(
        (context) => handleBackupAvailableV2(get, backup, context),
        'other'
      )
        .then(() => undefined)
        .catch(swallowAudioWorkCutoff);
    },
  });

  set({ backupCaptureSlot: { recordingId, recorder: backupCaptureRecorder } });
};

/** Idempotent — see `stopCaptureV2`. Persist-bound tail. */
export const stopBackupCaptureV2 = (
  set: SetState,
  get: GetState,
  recordingId?: string
): Promise<void> => {
  const expectedId = recordingId ?? get().activeRecordingKey?.recordingId;
  return stopCaptureSlot({
    slot: get().backupCaptureSlot,
    expectedId,
    alreadyRetiredEvent: 'STOP_BACKUP_RECORDER_ALREADY_RETIRED',
    mismatchEvent: 'STOP_BACKUP_RECORDER_RECORDING_MISMATCH',
    clear: () => set({ backupCaptureSlot: null }),
  });
};

/** Logout/teardown: stop backup capture without publishing the final blob. */
export const discardBackupCaptureSlotV2 = (set: SetState, get: GetState) => {
  get().backupCaptureSlot?.recorder.discard();
  set({ backupCaptureSlot: null });
};
